import React, { useEffect, useMemo, useState, useContext } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import Button from '../components/UI/Button';
import PhoneInput from '../components/UI/PhoneInput';
import BankInfoForm from '../components/Forms/BankInfoForm';
import { AuthContext } from '../context/AuthContext';
import { clearCompanyConfigCache } from '../utils/invoiceUtils';

const CompanyConfig = () => {
  const { user } = useContext(AuthContext);
  const canEdit = useMemo(() => {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    return perms.includes('ManageConfig') || perms.includes('CompanyConfig');
  }, [user]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [logoPreview, setLogoPreview] = useState('');
  const [form, setForm] = useState({
    companyName: '',
    namfisaRegNo: '',
    namfisaLicenseExpiry: '',
    principalOfficerName: '',
    address: '',
    phoneNumber: '',
    email: '',
    bank_name: '',
    branch_code: '',
    account_number: '',
    account_type: ''
  });
  const [logoFile, setLogoFile] = useState(null);


  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/company-config'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const cfg = res.data || {};
        setForm({
          companyName: cfg.company_name || '',
          namfisaRegNo: cfg.namfisa_reg_no || '',
          namfisaLicenseExpiry: cfg.namfisa_license_expiry || '',
          principalOfficerName: cfg.principal_officer_name || '',
          address: cfg.address || '',
          phoneNumber: cfg.phone_number || '',
          email: cfg.email || '',
          bank_name: cfg.bank_name || '',
          branch_code: cfg.branch_code || '',
          account_number: cfg.account_number || '',
          account_type: cfg.account_type || ''
        });
        // Use embedded base64 data URI to avoid cross-origin image fetches
        if (cfg.logo_base64) {
          setLogoPreview(cfg.logo_base64);
        } else {
          setLogoPreview('');
        }
        setMessage('');
        setLoading(false);
      } catch (err) {
        setMessage('Failed to load company configuration');
        setMessageType('error');
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const getMsgClass = () => {
    switch (messageType) {
      case 'success': return 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50';
      case 'error': return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50';
      default: return 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setLogoFile(file || null);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(String(reader.result || ''));
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('companyName', form.companyName);
      fd.append('namfisaRegNo', form.namfisaRegNo);
      fd.append('namfisaLicenseExpiry', form.namfisaLicenseExpiry || '');
      fd.append('principalOfficerName', (form.principalOfficerName || '').trim());
      fd.append('address', form.address);
      fd.append('phoneNumber', form.phoneNumber);
      fd.append('email', form.email);
      // Bank details
      fd.append('bank_name', form.bank_name);
      fd.append('branch_code', form.branch_code);
      fd.append('account_number', form.account_number);
      fd.append('account_type', form.account_type);
      if (logoFile) fd.append('logo', logoFile);

      await axios.put(apiUrl('/api/company-config'), fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // 1) Bust the in-memory cache that ProtectedRoute uses to decide
      //    whether to redirect admins to /company-config. Without this,
      //    every subsequent navigation re-redirects here for up to 5
      //    minutes (the cache TTL) even though the DB row is complete.
      clearCompanyConfigCache();

      // 2) Refetch the config so the form fields reflect what was actually
      //    persisted (rather than whatever the user typed). This makes a
      //    page refresh idempotent — the user can hit save once and see
      //    the same values after a reload.
      try {
        const freshCfg = await axios.get(apiUrl('/api/company-config'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const cfg = freshCfg.data || {};
        if (!cfg.missing) {
          setForm({
            companyName: cfg.company_name || '',
            namfisaRegNo: cfg.namfisa_reg_no || '',
            namfisaLicenseExpiry: cfg.namfisa_license_expiry || '',
            principalOfficerName: cfg.principal_officer_name || '',
            address: cfg.address || '',
            phoneNumber: cfg.phone_number || '',
            email: cfg.email || '',
            bank_name: cfg.bank_name || '',
            branch_code: cfg.branch_code || '',
            account_number: cfg.account_number || '',
            account_type: cfg.account_type || ''
          });
          if (cfg.logo_base64) setLogoPreview(cfg.logo_base64);
        }
      } catch { /* keep typed values if refetch fails */ }

      setLogoFile(null);
      setMessage('Company configuration saved');
      setMessageType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to save company configuration');
      setMessageType('error');
    }
    setSaving(false);
  };

  return (
    <div className="max-w-full min-h-screen">

      {message && (
        <div className={`mb-3 p-3 rounded border flex items-center justify-between ${getMsgClass()}`}>
          <span>{message}</span>
          <button
            onClick={() => setMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        </div>
      )}

      {loading ? (
        <div className="p-4 text-blue-700">Loading company details…</div>
      ) : (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Identity & Contacts */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 border border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-100 dark:border-slate-700">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9-4 9 4-9 4-9-4z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 10l-9 4-9-4m18 0v6l-9 4-9-4v-6" />
                  </svg>
                </div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Company Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Company Name</label>
                  <input
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    required
                    className={`w-full ${form.companyName.trim() ? '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20' : '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 !bg-rose-50 dark:!bg-rose-900/20 focus:ring-rose-500/20'}`}
                    placeholder="Company Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">NAMFISA Reg No</label>
                  <input
                    name="namfisaRegNo"
                    value={form.namfisaRegNo}
                    onChange={handleChange}
                    required
                    className={`w-full ${form.namfisaRegNo.trim() ? '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20' : '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 !bg-rose-50 dark:!bg-rose-900/20 focus:ring-rose-500/20'}`}
                    placeholder="e.g., ML/XXXX/XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">NAMFISA Licence Expiry Date</label>
                  <input
                    type="date"
                    name="namfisaLicenseExpiry"
                    value={form.namfisaLicenseExpiry}
                    onChange={handleChange}
                    required
                    className={`w-full ${form.namfisaLicenseExpiry ? '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20' : '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 !bg-rose-50 dark:!bg-rose-900/20 focus:ring-rose-500/20'}`}
                  />
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">The dashboard warns from 90 days before it expires.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Principal Officer Name</label>
                  <input
                    name="principalOfficerName"
                    value={form.principalOfficerName}
                    onChange={handleChange}
                    required
                    maxLength={150}
                    className={`w-full ${form.principalOfficerName.trim() ? '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20' : '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 !bg-rose-50 dark:!bg-rose-900/20 focus:ring-rose-500/20'}`}
                    placeholder="Full name of the principal officer"
                  />
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Used as 'Prepared by' on the Monthly Management Report.</p>
                </div>
                <div>
                  <PhoneInput
                    label="Phone Number"
                    value={form.phoneNumber}
                    onChange={(combined) => setForm(prev => ({ ...prev, phoneNumber: combined }))}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Address</label>
                  <input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    required
                    className={`w-full ${form.address.trim() ? '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20' : '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 !bg-rose-50 dark:!bg-rose-900/20 focus:ring-rose-500/20'}`}
                    placeholder="Physical or postal address"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className={`w-full ${form.email.trim() ? '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20' : '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 !bg-rose-50 dark:!bg-rose-900/20 focus:ring-rose-500/20'}`}
                    placeholder="info@company.com"
                  />
                </div>
              </div>
            </div>

            {/* Use the same BankInfoForm used by Add Client modal for identical format and dropdown items */}
            <BankInfoForm
              formData={form}
              setFormData={(next) => setForm(next)}
              formErrors={{}}
              readOnly={false}
            />
          </div>

          {/* Right: Logo */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 border border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-100 dark:border-slate-700">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                  </svg>
                </div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Branding</h2>
              </div>
              <div className="space-y-3">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Company Logo"
                    className="w-full max-h-36 object-contain rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 p-2"
                    onError={() => setLogoPreview('')}
                  />
                ) : (
                  <div className="h-36 flex items-center justify-center bg-gray-50 dark:bg-slate-700 text-gray-400 dark:text-slate-400 border border-gray-200 dark:border-slate-600 rounded-lg">No logo</div>
                )}
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50">
                  {logoPreview ? 'Replace logo' : 'Choose logo'}
                  <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </label>
                <p className="text-xs text-gray-500 dark:text-slate-400">Accepted: PNG, JPG, GIF, SVG, WEBP</p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={!canEdit || saving}
                loading={saving}
                busyLabel="Saving…"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default CompanyConfig;